/**
 * Returns a promise that would wait for a given time before resolving or rejecting
 * and will reject with an error if the timeout is reached
 *
 * @param promise Promise to wait for
 * @param time time in milliseconds to wait for the promise to resolve
 * @param errorMessage Error message to reject the promise with if the promise does not resolve within the given time
 */
export function timeout<T>(promise: Promise<T>, time: number, err: Error): Promise<T> {
    return internalTimeout<T>(promise, time, (_resolve, reject) => {
        reject(err);
    });
}

/**
 * Returns a promise that would wait for a given time before resolving or rejecting
 * and will resolve with undefined if the timeout is reached
 *
 * @param promise Promise to wait for
 * @param time time in milliseconds to wait for the promise to resolve
 */
export function safeTimeout<T>(promise: Promise<T>, time: number): Promise<T | undefined> {
    return internalTimeout<T | undefined>(promise, time, res => res(undefined));
}

function internalTimeout<T>(
    promise: Promise<T>,
    time: number,
    callback: (resolve: (value: T) => void, reject: (reason?: any) => void) => void
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            callback(resolve, reject);
        }, time);

        promise.then(
            value => {
                clearTimeout(timeoutId);
                resolve(value);
            },
            error => {
                clearTimeout(timeoutId);
                reject(error);
            }
        );
    });
}
